import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  orderBy,
  getDocs
} from 'firebase/firestore'
import { db, auth } from '@/firebase/config'
import { getSchoolInfo } from './adminService'
import { getUserByUid } from './userService'

export const createLog = async ({
  userId,
  collectionName,
  data,
  schoolId,
  action
}) => {
  const logRef = doc(collection(db, 'logs'))

  let schoolInfo = null
  try {
    schoolInfo =
      schoolId && schoolId !== 'system' ? await getSchoolInfo(schoolId) : null
  } catch {
    // School read may fail right after student registration due to rules
    // engine timing — log with 'Unknown' school name rather than aborting.
  }

  let userInfo = null
  try {
    userInfo = await getUserByUid(userId)
  } catch {
    // User read may fail in edge cases — proceed without user name.
  }

  const schoolName =
    userId === 'system' || schoolId === 'system'
      ? 'System'
      : (schoolInfo?.schoolName ?? 'Unknown')

  const userName = userInfo
    ? [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' ').trim() ||
      userId
    : userId

  await setDoc(logRef, {
    collection: collectionName,
    data,
    schoolId,
    userId,
    schoolName,
    userName,
    action,
    createdAt: serverTimestamp()
  })
}

export const getLogs = async () => {
  try {
    const q = query(collection(db, 'logs'), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    return []
  }
}

export const logError = async ({
  error,
  functionName,
  metadata = {},
  userId = null,
  schoolId = null
}) => {
  try {
    const currentUser = auth.currentUser
    const errorUserId = userId || currentUser?.uid || 'anonymous'

    const errorData = {
      errorMessage: error?.message || String(error),
      errorStack: error?.stack || null,
      errorName: error?.name || 'Error',
      functionName: functionName || 'unknown',
      ...metadata
    }

    if (error?.code) {
      errorData.errorCode = error.code
    }

    let userSchoolId = schoolId
    if (!userSchoolId && currentUser) {
      try {
        const userData = await getUserByUid(currentUser.uid)
        if (userData?.schoolId) {
          userSchoolId = userData.schoolId
        }
      } catch (fetchError) {
        // Silently handle user fetch error
      }
    }

    const logRef = doc(collection(db, 'logs'))

    let schoolInfo = null
    let userInfo = null

    try {
      if (userSchoolId && userSchoolId !== 'system') {
        schoolInfo = await getSchoolInfo(userSchoolId)
      }
      if (
        errorUserId &&
        errorUserId !== 'anonymous' &&
        errorUserId !== 'system'
      ) {
        userInfo = await getUserByUid(errorUserId)
      }
    } catch (infoError) {
      // Silently handle info fetch errors
    }

    const schoolName =
      errorUserId === 'system' || userSchoolId === 'system'
        ? 'System'
        : (schoolInfo?.schoolName ?? 'Unknown')

    await setDoc(logRef, {
      collection: 'errors',
      data: errorData,
      schoolId: userSchoolId || 'system',
      userId: errorUserId,
      schoolName,
      userName: userInfo
        ? `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() ||
          errorUserId
        : errorUserId,
      action: `error_${functionName}`,
      type: 'error',
      createdAt: serverTimestamp()
    })
  } catch (logError) {
    // Silently fail if logging itself fails to prevent infinite loops
  }
}
